import React, { useState, useEffect, useCallback } from 'react';
import { createSupportTicket, fetchMySupportTickets, fetchTicketMessages, replyToTicket } from '../apiService';
import './SupportPanel.css';

const TICKET_TYPES = ['Technical', 'Account', 'Billing', 'Other'];

/**
 * Support requests, shown inside Settings.
 *
 * Users submit a request and follow its status. Priority is deliberately not
 * offered: it is triaged by staff, and self-assigned priority tends to be
 * meaningless.
 */
function SupportPanel({ user }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState('Technical');
  const [tickets, setTickets] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openTicket, setOpenTicket] = useState(null);
  const [thread, setThread] = useState([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [reply, setReply] = useState('');
  const [sendingReply, setSendingReply] = useState(false);

  const loadTickets = useCallback(async () => {
    if (!user?.userId) { setLoading(false); return; }
    const res = await fetchMySupportTickets(user.userId);
    if (res.success) setTickets(res.data || []);
    setLoading(false);
  }, [user]);

  useEffect(() => { loadTickets(); }, [loadTickets]);

  async function handleSubmit(e) {
    e.preventDefault();
    setResult(null);

    if (!title.trim()) {
      setResult({ ok: false, msg: 'Give your request a short title.' });
      return;
    }
    if (!description.trim()) {
      setResult({ ok: false, msg: 'Describe what you need help with.' });
      return;
    }

    setSubmitting(true);
    const res = await createSupportTicket({
      userId: user.userId,
      title: title.trim(),
      description: description.trim(),
      type,
    });
    setSubmitting(false);

    if (res.success) {
      setResult({ ok: true, msg: `Request ${res.data.reference} submitted. We'll get back to you by email.` });
      setTitle('');
      setDescription('');
      setType('Technical');
      loadTickets();
    } else {
      setResult({ ok: false, msg: res.error || 'Could not submit your request.' });
    }
  }

  async function openThread(ticket) {
    setOpenTicket(ticket);
    setThread([]);
    setReply('');
    setThreadLoading(true);
    const res = await fetchTicketMessages(ticket.id, user.userId);
    if (res.success) setThread(res.data || []);
    setThreadLoading(false);
  }

  async function sendReply() {
    if (!reply.trim() || !openTicket) return;
    setSendingReply(true);
    const res = await replyToTicket(openTicket.id, user.userId, reply.trim());
    setSendingReply(false);
    if (res.success) {
      setThread(prev => [...prev, res.data]);
      setReply('');
      // Replying to a resolved ticket reopens it, so refresh the list.
      loadTickets();
    }
  }

  return (
    <div className="support-panel">
      <div className="support-form-card">
        <h3>Get help</h3>
        <p className="support-intro">
          Tell us what you need and we'll reply by email.
        </p>

        <form onSubmit={handleSubmit}>
          <div className="support-field">
            <label htmlFor="ticket-type">What's this about?</label>
            <select id="ticket-type" value={type} onChange={e => setType(e.target.value)}>
              {TICKET_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>

          <div className="support-field">
            <label htmlFor="ticket-title">Subject</label>
            <input
              id="ticket-title"
              type="text"
              value={title}
              placeholder="Short summary of the issue"
              onChange={e => setTitle(e.target.value)}
            />
          </div>

          <div className="support-field">
            <label htmlFor="ticket-description">Details</label>
            <textarea
              id="ticket-description"
              rows="5"
              value={description}
              placeholder="What happened, and what were you trying to do?"
              onChange={e => setDescription(e.target.value)}
            />
          </div>

          {result && (
            <div className={`support-result ${result.ok ? 'ok' : 'error'}`}>{result.msg}</div>
          )}

          <button type="submit" className="support-submit" disabled={submitting}>
            {submitting ? 'Submitting...' : 'Submit request'}
          </button>
        </form>
      </div>

      <div className="support-list-card">
        <h3>Your requests</h3>
        {loading ? (
          <p className="support-empty">Loading…</p>
        ) : tickets.length === 0 ? (
          <p className="support-empty">You haven't submitted any requests yet.</p>
        ) : (
          <table className="support-table">
            <thead>
              <tr>
                <th>Reference</th>
                <th>Subject</th>
                <th>Type</th>
                <th>Status</th>
                <th>Submitted</th>
              </tr>
            </thead>
            <tbody>
              {tickets.map(t => (
                <tr key={t.id} className="support-row-clickable" onClick={() => openThread(t)}>
                  <td className="support-ref">{t.reference}</td>
                  <td>{t.title}</td>
                  <td>{t.type}</td>
                  <td>
                    <span className={`support-status status-${(t.status || '').toLowerCase().replace(/\s+/g, '-')}`}>
                      {t.status}
                    </span>
                  </td>
                  <td>{t.date ? String(t.date).split(' ')[0] : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {openTicket && (
        <div className="support-thread-card">
          <div className="support-thread-header">
            <div>
              <span className="support-ref">{openTicket.reference}</span>
              <h3>{openTicket.title}</h3>
              <span className={`support-status status-${(openTicket.status || '').toLowerCase().replace(/\s+/g, '-')}`}>
                {openTicket.status}
              </span>
            </div>
            <button className="support-thread-close" onClick={() => setOpenTicket(null)}>Close</button>
          </div>

          <div className="support-message original">
            <div className="support-message-meta">
              <strong>You</strong>
              <span>{openTicket.date ? String(openTicket.date).split(' ')[0] : ''}</span>
            </div>
            <div className="support-message-body">{openTicket.description}</div>
          </div>

          {threadLoading ? (
            <p className="support-empty">Loading conversation…</p>
          ) : thread.length === 0 ? (
            <p className="support-empty">No replies yet. We'll respond here.</p>
          ) : (
            thread.map(m => (
              <div key={m.id} className={`support-message ${m.authorRole === 'Support' ? 'from-support' : ''}`}>
                <div className="support-message-meta">
                  <strong>{m.authorRole === 'Support' ? 'Zibit Support' : 'You'}</strong>
                  <span>{m.timestamp ? String(m.timestamp).split('.')[0].replace('T', ' ') : ''}</span>
                </div>
                <div className="support-message-body">{m.message}</div>
              </div>
            ))
          )}

          <div className="support-reply">
            <textarea
              rows="3"
              value={reply}
              placeholder="Reply to this ticket"
              onChange={e => setReply(e.target.value)}
            />
            <button
              className="support-submit"
              onClick={sendReply}
              disabled={sendingReply || !reply.trim()}
            >
              {sendingReply ? 'Sending...' : 'Send reply'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default SupportPanel;
